import { addDays, format, getISODay } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

import { Session } from "./types";

/**
 * Thursday time is the group's headline stat: time spent in a pub between
 * 3pm Thursday and 6am Friday (London time). A check-in counts in full for
 * those hours as long as, at some point in them, two bros were checked in to
 * that pub together (you among them if you're a bro, so a bro just needs
 * another bro to turn up). Anyone can earn it. It's worked out at checkout
 * and banked into `times` on the profile (the same buckets the group has
 * always raced on), while `anyTimes` counts every check-in, whenever it is.
 */
const TIMEZONE = "Europe/London";
const THURSDAY = 4;
export const THURSDAY_START_HOUR = 15;
export const THURSDAY_END_HOUR = 6;

interface Interval {
  start: number;
  end: number;
}

type TimedSession = Pick<Session, "id" | "profileId" | "startTime" | "endTime" | "locationName"> & {
  // Older sessions only recorded the venue's name, or a reference to it.
  venue?: (Pick<Session["venue"], "id"> & { placeId?: string | null }) | null;
  locationRef?: { id: string } | null;
};

/**
 * The ids a session's pub goes by: its venue, and the Google place behind it,
 * since the daily bonus pub is a venue of its own at a pub that's often also
 * listed as an ordinary one (see withBonusVenue).
 */
function getVenueIds(session: TimedSession) {
  const id = session.venue?.id || session.locationRef?.id;

  return id ? [id, session.venue?.placeId].filter(Boolean) : [];
}

/** Whether two sessions were at the same pub, as best older sessions can tell. */
export function isSameVenue(a: TimedSession, b: TimedSession) {
  const aIds = getVenueIds(a);
  const bIds = getVenueIds(b);

  if (aIds.length && bIds.length) {
    return aIds.some((id) => bIds.includes(id));
  }

  return !!a.locationName && a.locationName === b.locationName;
}

/** The Thursday 3pm – Friday 6am window that starts on the given London calendar day. */
function getWindowStartingOn(zonedDay: Date): Interval {
  const year = zonedDay.getFullYear();
  const month = zonedDay.getMonth();
  const date = zonedDay.getDate();

  return {
    start: fromZonedTime(new Date(year, month, date, THURSDAY_START_HOUR), TIMEZONE).getTime(),
    end: fromZonedTime(new Date(year, month, date + 1, THURSDAY_END_HOUR), TIMEZONE).getTime(),
  };
}

/** Every Thursday window that overlaps [start, end). */
export function getThursdayWindows(start: Date, end: Date): Interval[] {
  const windows: Interval[] = [];
  // Start a day early so a session in the small hours of Friday finds the
  // window that opened on Thursday afternoon.
  const last = toZonedTime(end, TIMEZONE);

  for (let day = addDays(toZonedTime(start, TIMEZONE), -1); day <= last; day = addDays(day, 1)) {
    if (getISODay(day) !== THURSDAY) continue;

    const window = getWindowStartingOn(day);

    if (window.start < end.getTime() && window.end > start.getTime()) {
      windows.push(window);
    }
  }

  return windows;
}

export function isInThursdayWindow(start: Date, end: Date) {
  return getThursdayWindows(start, end).length > 0;
}

/** The Thursday window `date` falls in, if it's in one. */
export function getCurrentThursdayWindow(date: Date): Interval | null {
  return getThursdayWindows(date, new Date(date.getTime() + 1))[0] ?? null;
}

/**
 * Whether `date` is on a Thursday night: 6am Thursday to 6am Friday, London
 * time, when sessions are cleared. Only a check-in on a Thursday night can
 * turn out to be a Thursday check-in, earning the full rate and bonuses.
 */
export function isThursdayNight(date: Date) {
  const zoned = toZonedTime(date, TIMEZONE);
  const day = zoned.getHours() < THURSDAY_END_HOUR ? addDays(zoned, -1) : zoned;

  return getISODay(day) === THURSDAY;
}

/**
 * The leaderboard buckets for the London day `date` falls on, other than
 * "Ttotal": its year ("T2026"), month ("T2026M10") and ISO week ("T2026W40").
 * All-week time is banked under the day each session started.
 */
export function getTimeKeys(date: Date) {
  const zoned = toZonedTime(date, TIMEZONE);

  return {
    year: format(zoned, "'T'yyyy"),
    month: format(zoned, "'T'yyyy'M'MM"),
    week: format(zoned, "'T'yyyy'W'II"),
  };
}

/**
 * The leaderboard buckets a Thursday's time is banked under, using the
 * same key format as `times`. Keyed by the Thursday itself, so time after
 * midnight still counts towards that Thursday's week, month and year.
 */
export function getThursdayTimeKeys(windowStart: number) {
  const { year, month, week } = getTimeKeys(new Date(windowStart));

  return ["Ttotal", year, month, week];
}

function mergeIntervals(intervals: Interval[]) {
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const merged: Interval[] = [];

  for (const interval of sorted) {
    const previous = merged[merged.length - 1];

    if (previous && interval.start <= previous.end) {
      previous.end = Math.max(previous.end, interval.end);
    } else {
      merged.push({ ...interval });
    }
  }

  return merged;
}

/** The most of `intervals` overlapping at once. */
function getPeakOverlap(intervals: Interval[]) {
  const events = intervals
    .flatMap((interval) => [
      { at: interval.start, change: 1 },
      { at: interval.end, change: -1 },
    ])
    .sort((a, b) => a.at - b.at || a.change - b.change);
  let present = 0;
  let peak = 0;

  for (const { change } of events) {
    present += change;
    peak = Math.max(peak, present);
  }

  return peak;
}

/** How much of `intervals` falls inside `window`. */
function overlapWith(intervals: Interval[], window: Interval) {
  return intervals.reduce(
    (sum, interval) =>
      sum +
      Math.max(0, Math.min(interval.end, window.end) - Math.max(interval.start, window.start)),
    0,
  );
}

function clipTo(intervals: Interval[], window: Interval) {
  return intervals
    .map((interval) => ({
      start: Math.max(interval.start, window.start),
      end: Math.min(interval.end, window.end),
    }))
    .filter((interval) => interval.start < interval.end);
}

/**
 * Each bro's time at the session's pub while the session was going (the
 * session's owner included, if they're a bro), merged so a bro with
 * back-to-back sessions still only counts once.
 */
function getBrosPresent(
  session: TimedSession,
  start: number,
  end: number,
  others: TimedSession[],
  isBro: (profileId: string) => boolean,
) {
  const brosPresent = new Map<string, Interval[]>();

  if (isBro(session.profileId)) {
    brosPresent.set(session.profileId, [{ start, end }]);
  }

  for (const other of others) {
    if (
      other.id === session.id ||
      other.profileId === session.profileId ||
      !isBro(other.profileId) ||
      !isSameVenue(session, other)
    ) {
      continue;
    }

    const interval = {
      start: Math.max(start, other.startTime.toMillis()),
      end: Math.min(end, other.endTime?.toMillis() ?? Infinity),
    };

    if (interval.start < interval.end) {
      brosPresent.set(other.profileId, [...(brosPresent.get(other.profileId) ?? []), interval]);
    }
  }

  for (const [profileId, intervals] of brosPresent) {
    brosPresent.set(profileId, mergeIntervals(intervals));
  }

  return brosPresent;
}

/**
 * Each Thursday window a finished session overlaps: how much of the session
 * was in it, the most bros in the pub at once while it was, and whether
 * that was enough (two) for the session's time in the window to count.
 */
function getWindowsWithBros(
  session: TimedSession,
  others: TimedSession[],
  isBro: (profileId: string) => boolean,
) {
  if (!session.endTime) {
    return [];
  }

  const start = session.startTime.toMillis();
  const end = session.endTime.toMillis();
  const brosPresent = [...getBrosPresent(session, start, end, others, isBro).values()];

  return getThursdayWindows(new Date(start), new Date(end)).map((window) => {
    const peakBros = getPeakOverlap(brosPresent.flatMap((bro) => clipTo(bro, window)));

    return {
      window,
      inHours: overlapWith([{ start, end }], window),
      peakBros,
      counts: peakBros >= 2,
    };
  });
}

/**
 * The Thursday time a finished session earns, as increments keyed like
 * `UserProfile.times`: all of its time inside a Thursday window, as long as
 * two bros were in the same pub together at some point during it (counting
 * the session's owner, if they're a bro). Another session that's still open
 * is treated as carrying on past this one's end.
 */
export function calculateThursdayTimes(
  session: TimedSession,
  others: TimedSession[],
  isBro: (profileId: string) => boolean,
): Record<string, number> {
  const times: Record<string, number> = {};

  for (const { window, inHours, counts } of getWindowsWithBros(session, others, isBro)) {
    if (counts && inHours > 0) {
      for (const key of getThursdayTimeKeys(window.start)) {
        times[key] = (times[key] ?? 0) + inHours;
      }
    }
  }

  return times;
}

/** Why a session earned the Thursday time it did. All times in ms. */
export interface ThursdayTimeExplanation {
  /** How long the session was. */
  total: number;
  /** The Thursday time it earned. */
  counted: number;
  /** Time outside 3pm Thursday to 6am Friday. */
  outsideHours: number;
  /** Time inside those hours that didn't count, as two bros were never in together. */
  withoutBros: number;
  /** The most bros in the pub at once during Thursday hours, counting the owner if they're one. */
  peakBros: number;
  /** "full" if all of it counted, "partial" if some did, "none" if none did. */
  status: "full" | "partial" | "none";
}

/**
 * Breaks down a finished session's time into what counted as Thursday time
 * and why the rest didn't, using the same rules as calculateThursdayTimes.
 * Returns null for a session that's still going.
 */
export function explainThursdayTime(
  session: TimedSession,
  others: TimedSession[],
  isBro: (profileId: string) => boolean,
): ThursdayTimeExplanation | null {
  if (!session.endTime) {
    return null;
  }

  const total = Math.max(0, session.endTime.toMillis() - session.startTime.toMillis());
  const windows = getWindowsWithBros(session, others, isBro);
  const inHours = windows.reduce((sum, window) => sum + window.inHours, 0);
  const counted = windows.reduce((sum, window) => sum + (window.counts ? window.inHours : 0), 0);
  const peakBros = windows.reduce((peak, window) => Math.max(peak, window.peakBros), 0);

  return {
    total,
    counted,
    outsideHours: total - inHours,
    withoutBros: inHours - counted,
    peakBros,
    status: counted <= 0 ? "none" : counted >= total ? "full" : "partial",
  };
}
